import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import classNames from "classnames";
import moment from "moment";
import { Button, ButtonGroup, SpinningButton } from "Components/ReactstrapSlim";
import { OwnerDetailsModal, OwnerCancelModal } from "Components/HourlyRequest";
import * as Icons from "Components/Icons";
import { Module, ModuleCaption } from "Components/Module";
import { ITeamMemberReservation, ReservationsClient, ReservationState, ReservationTimeType, ResponseWrapper, IReservationPager, ReservationPagerRequest, IReservationPagerRequest, ReservationVisitStatesClient, ReservationPagesClient } from "Services/API/API";
import { useApiClient } from "Services/API/useApiClient";
import { Attendance } from "./Attendance";
import { ReservationRow } from "../ReservationRow";
import { useTitle } from "../Layout/Mobility3/Main";
import UserContext from "../../UserContext";
import { DatePickerInputControlled } from "Components/Inputs";
import { toDate } from "Utils/date";
import { ReservationDetailsModal } from "../Reservations/DetailsModal";


export type FilterTime = {
   startTime?: string | undefined,
   endTime?: string | undefined
}

interface RequestActionState {
   action: string,
   requestId: Guid
}

const PAGE_SIZE = 20;
const UPCOMING_FIRST_CHUNK_SIZE = 3;
const PAST_FIRST_CHUNK_SIZE = 5;

function setFilterDatesToFilter(filter: IReservationPagerRequest, filterDates: FilterTime) {
   filter.reservationStartTimeFilter = filterDates.startTime
      ? moment(filterDates.startTime).startOf("day").toISOString()
      : undefined;

   filter.reservationEndTimeFilter = filterDates.endTime
      ? moment(filterDates.endTime).endOf("day").subtract(1, "millisecond").toISOString()
      : undefined;
}

function getReservationPagerRequest(filterDates: FilterTime, reservationTimeType: ReservationTimeType, memberId: Guid) {
   const filter = ReservationPagerRequest.fromJS({
      reservationTimeType,
      memberId,
      countOnPage: PAGE_SIZE,
      reservationStartTimeFilter: undefined,
      reservationEndTimeFilter: undefined
   });
   setFilterDatesToFilter(filter, filterDates);
   return filter;
}
function getReservationPageRequest(pager: IReservationPager, pageNumber: number) {
   if (pager?.data && pageNumber >= 0 && pageNumber < pager.data.length) {
      return pager.data[pageNumber];
   }
}

function usePager(reservationTimeType: ReservationTimeType) {
   const reservationsClient = useApiClient(ReservationsClient);
   const reservationPagesClient = useApiClient(ReservationPagesClient);

   const { user } = useContext(UserContext);
   const [isLoading, setLoading] = useState(true);
   const isLoadingRef = useRef(isLoading);
   isLoadingRef.current = isLoading;
   const [pager, setPager] = useState<IReservationPager>();
   const [pageNumber, setPageNumber] = useState(0);
   const [reservations, setReservations] = useState<ITeamMemberReservation[]>([]);

   const formMethods = useForm<FilterTime>();
   const [filterDates, setFilterDates] = useState<FilterTime>({
      startTime: undefined,
      endTime: undefined
   });

   useEffect(() => {
      const subscription = formMethods.watch(values => {
         setFilterDates(prevValues => {
            if (values.startTime != prevValues.startTime || values.endTime != prevValues.endTime) {
               return values;
            }
            else {
               return prevValues;
            }
         })
      });

      return () => subscription.unsubscribe();
   }, [formMethods.watch]);


   const isNextPageAvailable = pageNumber + 1 < (pager?.data?.length ?? 0);

   async function loadNextPage() {
      const nextPageNumber = pageNumber + 1;
      if (isNextPageAvailable && isLoadingRef.current === false && pager) {
         const filters = getReservationPageRequest(pager, nextPageNumber);
         if (filters) {
            try {
               setLoading(true);
               const nextReservations = await reservationPagesClient.page(filters);
               setReservations([...reservations, ...nextReservations]);
               setPageNumber(nextPageNumber);
            }
            finally {
               setLoading(false);
            }
         }

      }
   }

   const reloadPager = async (abortSignal?: AbortSignal) => {
      const filter = getReservationPagerRequest(filterDates, reservationTimeType, user.id);
      setPager(await reservationPagesClient.pager(user.id, filter, abortSignal));
   }

   useEffect(() => {
      const abortController = new AbortController();
      reloadPager(abortController.signal);
      return () => abortController.abort();
   }, [filterDates]);

   const reloadReservationsInner = async (abortSignal?: AbortSignal, reloadPagerIfNoFilters?: boolean) => {
      if (pager) {
         const filters = getReservationPageRequest(pager, 0);
         if (filters) {
            try {
               setLoading(true);
               setReservations([]);
               const nextReservations = await reservationPagesClient.page(filters, abortSignal);
               setReservations(nextReservations);
               setPageNumber(0);
            }
            finally {
               setLoading(false);
            }
         } else {
            setLoading(false);
            if (reloadPagerIfNoFilters) {
               reloadPager(abortSignal);
            }
         }
      }
   }

   const reloadReservations = async (abortSignal?: AbortSignal) => {
      await reloadPager(abortSignal);
   }

   useEffect(() => {
      const abortController = new AbortController();
      reloadReservationsInner(abortController.signal);
      return () => abortController.abort();
   }, [pager]);

   const updateReservation = async (reservation: ITeamMemberReservation) => {
      try {
         const updatedReservation = await reservationsClient.get(reservation.idForLink, reservation.occurrenceDate, undefined);
         setReservations((reservaitons) => {
            return updatedReservation
               ? reservations.map(item => {
                  if (item.idForLink == reservation.idForLink && (!reservation.occurrenceDate || moment(reservation.occurrenceDate).isSame(item.occurrenceDate))) {
                     return updatedReservation;
                  }
                  return item;
               })
               : reservaitons;
         });
      }
      catch (ex) {
         const error = ex as ResponseWrapper;
         console.log(error?.errorMessage ?? "Unhandled exception.");
      }
   }

   // reservations, reservationsCount, isLoading, loadNextPage
   return [reservations, pager?.total ?? 0, isLoading, isNextPageAvailable ? loadNextPage : undefined, updateReservation, reloadReservations,
      formMethods, filterDates] as const;
}

export const Reservations: React.FC = () => {
   useTitle("Reservations");

   const [isUpcomingCollapsed, setIsUpcomingCollapsed] = useState(false);
   const [isPastCollapsed, setIsPastCollapsed] = useState(false);
   const [initialRequestAction, setInitialRequestAction] = useState<RequestActionState | undefined>(undefined);
   const [hrtbCancelRequestId, setHrtbCancelRequestId] = useState<Guid | undefined>(undefined);
   const [hrtbDetailsRequestId, setHrtbDetailsRequestId] = useState<Guid | undefined>(undefined);
   const [reservationDetails, setReservationDetails] = useState<ITeamMemberReservation | undefined>(undefined);

   const reservationVisitStatesClient = useApiClient(ReservationVisitStatesClient);

   const [upcomingReservations, upcomingReservationsCount, isUpcomingLoading, loadNextUpcomingPage, updateUpcomingReservation, reloadUpcomingReservations] = usePager(ReservationTimeType.Future);
   const [pastReservations, pastReservationsCount, isPastLoading, loadNextPastPage, updatePastReservation, reloadPastReservations, pastFilterTimeForm, pastDateRange] = usePager(ReservationTimeType.Past);

   const unattendedReservations = useMemo(() => pastReservations.filter(r => r.reservationState == ReservationState.Future && !r.memberStars && !r.deleted), [pastReservations]);
   const [unattendedReservationIndex, setUnattendedReservationIndex] = useState(0);
   useEffect(() => {
      if (unattendedReservationIndex >= unattendedReservations.length && unattendedReservations.length > 0) {
         setUnattendedReservationIndex(unattendedReservations.length - 1);
      }
   }, [unattendedReservationIndex, unattendedReservations]);

   const [isUpcomingOnlyFirstChunkVisible, setUpcomingOnlyFirstChunkVisible] = useState(true);
   const [isPastOnlyFirstChunkVisible, setPastOnlyFirstChunkVisible] = useState(true);

   const updateData = () => {
      reloadUpcomingReservations();
      reloadPastReservations();
   }

   const findReservation = (reservations: ITeamMemberReservation[], reservationId?: Guid, occurrenceDate?: string): ITeamMemberReservation | undefined => {
      return reservations.find(r => r.idForLink == reservationId && (!occurrenceDate || moment(occurrenceDate).isSame(r.occurrenceDate)))
   }

   const updateReservation = async (reservationDetails: ITeamMemberReservation, updatedReservationId?: Guid, updatedOccurrenceDate?: string) => {
      if (findReservation(upcomingReservations, updatedReservationId, updatedOccurrenceDate))
         updateUpcomingReservation(reservationDetails);
      else if (findReservation(pastReservations, updatedReservationId, updatedOccurrenceDate))
         updatePastReservation(reservationDetails);
      else
         updateData();
   }

   const updateReservationDetails = (reservations: ITeamMemberReservation[]) => {
      if (reservationDetails) {
         const r = findReservation(reservations, reservationDetails.idForLink, reservationDetails.occurrenceDate);
         if (r) {
            setReservationDetails(r);
            return true;
         }
      }
      return false;
   }

   const setUnattendedReservation = (index: number) => {
      if (index < unattendedReservations.length && index >= 0) {
         setUnattendedReservationIndex(index);
      }
   }

   const changeReservationState = async (wasVisited: boolean) => {
      if (unattendedReservationIndex < unattendedReservations.length && unattendedReservationIndex > -1) {
         try {
            const reservation = unattendedReservations[unattendedReservationIndex];
            const result = await reservationVisitStatesClient.set(reservation.idForLink, wasVisited, reservation.occurrenceDate);
            if (result.success) {
               await updatePastReservation(reservation);
            }
         } catch (ex) {
            const error = ex as ResponseWrapper;
            console.log(error?.errorMessage ?? "Unhandled exception.");
         }
      }
   }



   useEffect(() => {
      const urlParams = new URLSearchParams(window.location.search);
      const requestId = urlParams.get("rId");
      const requestAction = urlParams.get("rAction");
      if (requestId && requestAction) {
         setInitialRequestAction({
            action: requestAction,
            requestId
         })
      }
   }, [])

   useEffect(() => {
      if (initialRequestAction && !isUpcomingLoading) {
         if (initialRequestAction.action == LSCommon.Enums.RequestAction.getKey(LSCommon.Enums.RequestAction.ViewDetails)) {
            setHrtbDetailsRequestId(initialRequestAction.requestId);
         }
         if (initialRequestAction.action == LSCommon.Enums.RequestAction.getKey(LSCommon.Enums.RequestAction.Cancel)) {
            setHrtbCancelRequestId(initialRequestAction.requestId);
         }

         setInitialRequestAction(undefined);
      }
   }, [initialRequestAction, isUpcomingLoading])

   useEffect(() => {
      if (!updateReservationDetails(upcomingReservations) && !updateReservationDetails(pastReservations)) {
         //in case we cannot update inspected reservation - close the details modal
         setReservationDetails(undefined);
      }
   }, [upcomingReservations, pastReservations])

   return (<><Module loading={(upcomingReservations.length == 0 && isUpcomingLoading) || (pastReservations.length == 0 && isPastLoading)} size="lg" className={classNames("module-no-reservations", { "d-none": upcomingReservations.length > 0 || pastReservations.length > 0 })}>
      <ModuleCaption className="title">No Reservations Yet</ModuleCaption>
      <div className="subtitle">Your reservation will appear here when you book it.</div>
      <div className="btn-wrapper">
         <Button color="arctic-20" size="sm" onClick={() => window.open("/search", "_blank")}>Search for spaces nearby</Button>
      </div>
   </Module>
      {unattendedReservations.length > 0 && unattendedReservationIndex < unattendedReservations.length && <Module loading={isPastLoading} size="lg" className="module-attendance" key={unattendedReservationIndex}>
         <div className="header-wrapper">
            <div className="caption">Please leave feedback on your recent experience.</div>
            <div className="caption-mobile">You have some actions to take</div>
            <ButtonGroup className="pager-wrapper">
               <Button color="arctic-20" size="sm" onClick={() => setUnattendedReservation(unattendedReservationIndex - 1)} disabled={unattendedReservationIndex <= 0}><Icons.ArrowLeft /></Button>
               <span>{unattendedReservationIndex + 1}/{unattendedReservations.length}</span>
               <Button color="arctic-20" size="sm" onClick={() => setUnattendedReservation(unattendedReservationIndex + 1)} disabled={unattendedReservationIndex >= unattendedReservations.length - 1}><Icons.ArrowRight /></Button>
            </ButtonGroup>
         </div>
         <Attendance reservation={unattendedReservations[unattendedReservationIndex]}
            changeReservationState={changeReservationState}
            onLeaveReview={updatePastReservation} />
      </Module>}
      <Module loading={isUpcomingLoading && upcomingReservations.length == 0} size="lg" className={classNames("module-upcoming-reservations", { "d-none": upcomingReservations.length == 0 })}>
         <div className="reservations-menu-wrapper">
            <div className="title">Upcoming reservations {upcomingReservationsCount ? `(${upcomingReservationsCount})` : null}</div>
            <div className="collapse-wrapper">
               <Button size="sm" color="secondary" onClick={() => setIsUpcomingCollapsed(isCollapsed => !isCollapsed)}>{isUpcomingCollapsed ? <Icons.ArrowDropDown /> : <Icons.ArrowDropUp />}</Button>
            </div>
         </div>
         <div className={classNames("reservations-wrapper", { "d-none": isUpcomingCollapsed })}>
            {
               upcomingReservations
                  .slice(0, isUpcomingOnlyFirstChunkVisible ? UPCOMING_FIRST_CHUNK_SIZE : undefined)
                  .map(reservation => <ReservationRow key={`${reservation.idForLink}${reservation.occurrenceDate}`} reservation={reservation} reservationType={ReservationTimeType.Future} onUpdateReservationList={updateData} onChange={updateUpcomingReservation} onHourlyRequestToBookCancel={setHrtbCancelRequestId} onHourlyRequestToBookDetails={setHrtbDetailsRequestId} onReservationDetails={setReservationDetails} />)
            }
         </div>
         {
            !isUpcomingCollapsed
               ? isUpcomingOnlyFirstChunkVisible && upcomingReservations.length > UPCOMING_FIRST_CHUNK_SIZE
                  ? <div className="load-more-wrapper">
                     <Button color="secondary" onClick={() => setUpcomingOnlyFirstChunkVisible(false)}>Show more</Button>
                  </div>
                  : loadNextUpcomingPage
                     ? <div className="load-more-wrapper">
                        <SpinningButton color="secondary" isLoading={isUpcomingLoading} onClick={() => loadNextUpcomingPage()}>Show more</SpinningButton>
                     </div>
                     : null
               : null
         }
      </Module>
      <Module loading={isPastLoading && pastReservations.length == 0} size="lg" className={classNames("module-past-reservations", { "d-none": pastReservations.length == 0 })}>
         <div className="reservations-menu-wrapper">
            <div className="past-wrapper">
               <div className="title">Past reservations {pastReservationsCount ? `(${pastReservationsCount})` : null}</div>
               <div className="date-picker-wrapper">
                  <div className="start-time-wrapper">
                     <div className="caption">From</div>
                     <DatePickerInputControlled maxDate={pastDateRange?.endTime ? toDate(pastDateRange.endTime) : undefined} control={pastFilterTimeForm.control} name="startTime" bsSize="sm" placeholderText="Anytime" />
                  </div>
                  <div className="end-time-wrapper">
                     <div className="caption last">To</div>
                     <DatePickerInputControlled minDate={pastDateRange?.startTime ? toDate(pastDateRange.startTime) : undefined} control={pastFilterTimeForm.control} name="endTime" bsSize="sm" placeholderText="Anytime" />
                  </div>
               </div>
            </div>
            <div className="collapse-wrapper">
               <Button size="sm" color="secondary" onClick={() => setIsPastCollapsed(isCollapsed => !isCollapsed)}>{isPastCollapsed ? <Icons.ArrowDropDown /> : <Icons.ArrowDropUp />}</Button>
            </div>
         </div>
         <div className={classNames("reservations-wrapper", { "d-none": isPastCollapsed })}>
            {
               pastReservations
                  .slice(0, isPastOnlyFirstChunkVisible ? PAST_FIRST_CHUNK_SIZE : undefined)
                  .map(reservation => <ReservationRow key={`${reservation.idForLink}${reservation.occurrenceDate}`} reservation={reservation} reservationType={ReservationTimeType.Past} onChange={updatePastReservation} onHourlyRequestToBookCancel={setHrtbCancelRequestId} onHourlyRequestToBookDetails={setHrtbDetailsRequestId} onReservationDetails={setReservationDetails} />)
            }
         </div>
         {
            !isPastCollapsed
               ? isPastOnlyFirstChunkVisible && pastReservations.length > PAST_FIRST_CHUNK_SIZE
                  ? <div className="load-more-wrapper">
                     <Button color="secondary" onClick={() => setPastOnlyFirstChunkVisible(false)}>Show more</Button>
                  </div>
                  : loadNextPastPage
                     ? <div className="load-more-wrapper">
                        <SpinningButton color="secondary" isLoading={isPastLoading} onClick={() => loadNextPastPage()}>Show more</SpinningButton>
                     </div>
                     : null
               : null
         }
      </Module>
      {hrtbCancelRequestId && <OwnerCancelModal requestId={hrtbCancelRequestId} isOpen={!!hrtbCancelRequestId} toggle={() => setHrtbCancelRequestId(undefined)} onSuccess={() => updateData()} />}
      {hrtbDetailsRequestId && <OwnerDetailsModal requestId={hrtbDetailsRequestId} isOpen={!!hrtbDetailsRequestId} toggle={() => setHrtbDetailsRequestId(undefined)} />}
      {reservationDetails && <ReservationDetailsModal reservation={reservationDetails} isOpen={!!reservationDetails} toggle={() => setReservationDetails(undefined)} onUpdateReservation={(updatedReservationId, updatedOccurrenceDate) => { updateReservation(reservationDetails, updatedReservationId, updatedOccurrenceDate); }} onUpdateReservationList={updateData} />}
   </>);
}